import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

import { BaseButton, FormikField } from 'src/components/common';
import { ExpertisesMultiSelect } from 'src/components/fields/selects/ExpertisesMultiSelect/ExpertisesMultiSelect';
import { ExpertSelect } from 'src/components/fields/selects/ExpertSelect/ExpertSelect';

import { useFormikErrorsHandler } from 'src/utils/hooks/useFormikErrorsHandler';
import { IBaseForm } from 'src/utils/types/forms';
import { ICreateGuestModel } from 'src/api/base/Guest/types';

import { newNewsValidationSchema } from './newNewsValidationSchema';
import { isEditingNewsForm } from 'src/utils/helpers';
import { FilesUploader } from 'src/components/common/FilesUploader';
import { BaseSunEditor } from 'src/components/common/BaseSunEditor';

import { ExpertisesApi } from 'src/api/base/Expertises';
import FormikHierarchySelect from 'src/features/user/journalist/profile/JournalistInfoForm/FormikHierarchySelect';

const useStyles = makeStyles((theme) => ({
  formGroups: {
    marginTop: '20px',
  },
  formGroup: {
    display: 'grid',
    gridGap: '20px',
    gridAutoRows: 'max-content',
  },
  submitSection: {
    marginTop: '20px',
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(100px, 200px)',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gridGap: '20px',
    },
  },
  available: {
    padding: '23px',
  },
  ps: {
    margin: '0',
  },
  gray: {
    margin: '0',
    color: '#9EA8AB',
  },
  formRowN: {
    display: 'grid',
    gridGap: '20px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

type PNewContactForm = IBaseForm<ICreateGuestModel> & {
  shouldResetAfterSuccess?: boolean;
  newsTemplate?: {
    created_at: string;
    expertise: [];
    id: number;
    link: string;
    text: string;
    title: string;
    updated_at: string;
    picture: any;
  };
};

const formInitialValues = {
  title: '',
  link: '',
  text: '',
  expertise: [],
  guest_id: [],
  picture: '',
};

const formKeys = Object.keys(formInitialValues);

export const NewNewsForm: React.FC<PNewContactForm> = (props) => {
  const { onSubmit, loading, errors, shouldResetAfterSuccess, newsTemplate }:any =
    props;    

  const history = useHistory();

  const classes = useStyles();

  const isEditForm = isEditingNewsForm(history.location.pathname);
  const formik = useFormik<any>({
    initialValues:  newsTemplate ? { ...newsTemplate } : { ...formInitialValues }, 
    validationSchema: newNewsValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append('picture', values.picture || []);
      formData.set('expertise', JSON.stringify(values.expertise));
      formData.set('guest_id', JSON.stringify(values.guest_id));
      formData.append('link', values.link);
      formData.append('text', values.text);
      formData.append('title', values.title);

      formik.setErrors({});
      onSubmit(formData);
    },
  });
  const { setFieldError, resetForm, setFieldValue } = formik;
  const handleChooseFile = (files: (File | string)[]) => {
    const file = files[0];
    formik.setFieldValue('picture', file);
  };
  useFormikErrorsHandler({ errors, setFieldError });

  useEffect(() => {
    if (shouldResetAfterSuccess) {
      resetForm();
      history.goBack();
    }
  }, [shouldResetAfterSuccess, resetForm, history]);

  useEffect(() => {
    if (newsTemplate) {
      formKeys.forEach((key) => {
        // @ts-ignore
        setFieldValue(key, newsTemplate[key]);
      });
    }
  }, [setFieldValue, newsTemplate]);
  
  const [expertiseTree, setExpertiseTree] = useState([]);
  	
	const loadExpertises = async () => {
	  try {
		const expertiseresponse = await ExpertisesApi.getExpertisesTree();

		if (expertiseresponse) {
		  setExpertiseTree(expertiseresponse.data.data || []);
		} else {
		  setExpertiseTree([]);
		}

	  } catch (error) {
		setExpertiseTree([]);
	  }
	};
	
	useEffect(() => {
	  loadExpertises();
	}, []);
	
  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className={classes.formGroups}>
          <div className={classes.formGroup}>
            <FormikField label="Title*" name="title" formik={formik} />
			<FormikHierarchySelect
				name="expertise"
				label="Areas of expertise *"
				formik={formik}
				options={expertiseTree}
			/>
            <h2 className={classes.ps}>Upload image</h2>
            <p className={classes.gray}>
              We recommend keep image to at least 1000 pixels wide and 16:9
              aspect ratio. Accepts .jpeg, .jpg, .png, .gif, .mp4 files only. Up
              to 20MB.
            </p>
            <FilesUploader
              disabled={formik.values.picture}
              onChooseFile={handleChooseFile}
              files={formik.values.picture}
              accept=".png,.jpeg,.jpg,video/*"
              id="picture"
              name="picture"
              error={formik.errors.picture && formik.touched.picture}
              helperText={formik.errors.picture}
            />

            <div className={classes.formRowN}>
              <BaseSunEditor
                defaultValue={formik.values.text}
                name="text"
                onChange={(value) => {
                  formik.setFieldValue('text', value);
                }}
              />
            </div>

           
            <FormikField label="Link*" name="link" formik={formik} />
			{ /* <FormikField
              name="expertise"
              formik={formik}
              label="Areas of expertise *"
              inputComponent={ExpertisesMultiSelect}
              onChange={(value: any) => {
                formik.setFieldValue('expertise', value);
              }}
            /> */ }
            <FormikField
              name="guest_id"
              formik={formik}
              label="Expert *"
              inputComponent={ExpertSelect}
              isMulti
              onChange={(value: any) => {
                formik.setFieldValue('guest_id', value);
              }}
            />
          </div>
          <div className={classes.submitSection}>
            <BaseButton variant="contained" type="submit" loading={loading}>
              {isEditForm ? ' Save changes' : 'Create'}
            </BaseButton>
          </div>
        </div>
      </form>
    </div>
  );
};
